import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { Plus, Trash2, FileSignature, Receipt, Lock, TrendingUp, DollarSign, CheckCircle2, Paperclip, Eye, X, Upload } from "lucide-react";

const CATEGORY_OPTIONS = ["Labor", "Materials", "Subcontractor", "Travel", "Software", "Marketing", "Misc"];
const fmt = (n, ccy) => new Intl.NumberFormat(undefined, { style: "currency", currency: ccy || "SGD" }).format(n || 0);
const RECEIPT_ACCEPT = "image/jpeg,image/png,image/webp,image/gif,image/heic,application/pdf,.jpg,.jpeg,.png,.webp,.gif,.heic,.pdf";

// Fires a full-screen confetti "project shipped" burst. Called once per deal
// the first time we observe finalized_at flipping from null → set.
function fireProjectShippedConfetti() {
  const duration = 2500;
  const end = Date.now() + duration;
  const colors = ["#25D366", "#FBBF24", "#38BDF8", "#A78BFA", "#F472B6"];
  (function frame() {
    confetti({ particleCount: 6, angle: 60, spread: 55, origin: { x: 0, y: 0.7 }, colors });
    confetti({ particleCount: 6, angle: 120, spread: 55, origin: { x: 1, y: 0.7 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  // Big burst overhead at t=0 and t=250ms
  confetti({ particleCount: 160, spread: 100, startVelocity: 55, origin: { x: 0.5, y: 0.35 }, colors });
  setTimeout(() => confetti({ particleCount: 120, spread: 120, startVelocity: 45, origin: { x: 0.5, y: 0.2 }, colors }), 250);
}

export default function DealCostSummary({ dealId }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: summary } = useQuery({
    queryKey: ["cost-summary", dealId],
    queryFn: () => api.get(`/deals/${dealId}/cost-summary`).then((r) => r.data),
  });
  const { data: expenses = [] } = useQuery({
    queryKey: ["expenses", dealId],
    queryFn: () => api.get(`/deals/${dealId}/expenses`).then((r) => r.data),
  });
  const { data: quotes = [] } = useQuery({
    queryKey: ["quotations", "for-deal", dealId],
    queryFn: () => api.get("/quotations", { params: { deal_id: dealId } }).then((r) => r.data),
  });

  const [openExp, setOpenExp] = useState(false);
  const [pendingReceipt, setPendingReceipt] = useState(null); // File chosen in the dialog before the expense exists
  const [expForm, setExpForm] = useState({ description: "", amount: "", category: "Misc", incurred_at: new Date().toISOString().slice(0, 10), vendor: "", reference: "", notes: "", is_unplanned: false });

  const finalised = !!summary?.finalized_at;
  const closeReady = summary?.close_ready && !finalised;

  // Fire confetti once, on the transition from "not finalized" → "finalized".
  const prevFinalisedRef = useRef(null);
  useEffect(() => {
    if (!summary) return;
    const isNow = !!summary.finalized_at;
    if (prevFinalisedRef.current === false && isNow) {
      fireProjectShippedConfetti();
      toast.success("Project shipped — cost summary finalised.", { duration: 6000 });
    }
    prevFinalisedRef.current = isNow;
  }, [summary?.finalized_at]);

  const addExpense = useMutation({
    mutationFn: async () => {
      const created = await api.post(`/deals/${dealId}/expenses`, {
        ...expForm,
        amount: parseFloat(expForm.amount) || 0,
      }).then((r) => r.data);
      // If the user picked a receipt in the dialog, upload it right after creation
      if (pendingReceipt) {
        const fd = new FormData();
        fd.append("file", pendingReceipt);
        await api.post(`/expenses/${created.id}/receipt`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      return created;
    },
    onSuccess: () => {
      toast.success(pendingReceipt ? "Expense + receipt saved" : "Expense added");
      setOpenExp(false);
      setPendingReceipt(null);
      setExpForm({ description: "", amount: "", category: "Misc", incurred_at: new Date().toISOString().slice(0, 10), vendor: "", reference: "", notes: "", is_unplanned: false });
      qc.invalidateQueries({ queryKey: ["expenses", dealId] });
      qc.invalidateQueries({ queryKey: ["cost-summary", dealId] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed to add expense"),
  });

  const delExpense = useMutation({
    mutationFn: (expenseId) => api.delete(`/expenses/${expenseId}`),
    onSuccess: () => {
      toast.success("Expense removed");
      qc.invalidateQueries({ queryKey: ["expenses", dealId] });
      qc.invalidateQueries({ queryKey: ["cost-summary", dealId] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  const totals = summary?.totals || {};
  const currency = summary?.currency || "SGD";

  return (
    <div className="space-y-4">
      {/* Quotations for this deal */}
      <Card className="rounded-sm bg-card border-border p-6" data-testid="deal-quotations-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileSignature className="h-4 w-4 text-primary" />
            <div className="font-display text-xl font-extrabold">Quotations</div>
            <Badge variant="secondary" className="rounded-sm text-[10px]">{quotes.length}</Badge>
          </div>
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => navigate(`/quotations/new?deal_id=${dealId}`)} data-testid="deal-new-quote">
            <Plus className="h-4 w-4 mr-1.5" /> New quotation
          </Button>
        </div>
        {quotes.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">No quotations yet.</div>
        ) : (
          <div className="space-y-2">
            {quotes.map((q) => (
              <Link key={q.id} to={`/quotations/${q.id}`}
                    className="flex items-center justify-between gap-3 rounded-sm border border-border bg-secondary/20 hover:bg-secondary/40 px-3 py-2 transition-colors"
                    data-testid={`deal-quote-${q.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs text-primary">{q.quote_number}</div>
                  <div className="text-xs text-muted-foreground truncate">{q.bill_to?.name} · {q.issue_date}</div>
                </div>
                <div className="text-sm font-semibold">{fmt(q.total, q.currency)}</div>
                <Badge className="rounded-sm text-[10px] capitalize">{q.status}</Badge>
              </Link>
            ))}
          </div>
        )}
      </Card>

      {/* Cost summary + close-ready banner */}
      <Card className="rounded-sm bg-card border-border p-6" data-testid="deal-cost-summary">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            <div className="font-display text-xl font-extrabold">Cost Summary</div>
            {finalised && (
              <Badge className="rounded-sm text-[10px] bg-green-500/10 text-green-500 border-green-500/40" data-testid="cost-summary-finalized">
                <Lock className="h-3 w-3 mr-1" /> Finalised {new Date(summary.finalized_at).toLocaleDateString()}
              </Badge>
            )}
          </div>
          {closeReady && (
            <Badge className="rounded-sm text-[10px] bg-primary/10 text-primary border-primary/40 animate-pulse" data-testid="close-ready-badge">
              <CheckCircle2 className="h-3 w-3 mr-1" /> Ready to close — mark last task done to finalise
            </Badge>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <MiniKpi label="Quoted" value={fmt(totals.quoted, currency)} />
          <MiniKpi label="Paid" value={fmt(totals.paid, currency)} tone="green" />
          <MiniKpi label="Expenses" value={fmt(totals.expenses, currency)} tone="red" />
          <MiniKpi label="Profit" value={fmt(totals.profit, currency)} tone={totals.profit >= 0 ? "green" : "red"} sub={totals.margin_pct != null ? `${totals.margin_pct}% margin` : "—"} />
        </div>

        {summary?.tasks && (
          <div className="text-xs text-muted-foreground mb-3" data-testid="cost-summary-tasks-progress">
            Tasks: <span className="text-foreground font-semibold">{summary.tasks.done} / {summary.tasks.total}</span> done ·
            Invoices: <span className={`font-semibold ${summary.invoices?.any_paid ? "text-green-400" : "text-red-400"}`}> {summary.invoices?.any_paid ? "Paid ✓" : "None paid yet"}</span>
          </div>
        )}

        {/* By category */}
        {(summary?.by_category || []).length > 0 && (
          <div className="mb-4 space-y-1.5" data-testid="cost-summary-by-category">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">By category</div>
            {summary.by_category.map((c) => (
              <div key={c.category} className="flex items-center justify-between text-sm">
                <div className="flex-1 truncate">{c.category} <span className="text-muted-foreground text-xs">({c.count})</span></div>
                <div className="font-semibold">{fmt(c.total, currency)}</div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Expense list */}
      <Card className="rounded-sm bg-card border-border p-6" data-testid="deal-expenses-list">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Receipt className="h-4 w-4 text-primary" />
            <div className="font-display text-lg font-extrabold">Expenses</div>
          </div>
          {!finalised && (
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setOpenExp(true)} data-testid="deal-add-expense">
              <Plus className="h-4 w-4 mr-1.5" /> Log expense
            </Button>
          )}
        </div>
        {expenses.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">No expenses yet. Log receipts, subcontractor bills or any unplanned costs.</div>
        ) : (
          <div className="grid grid-cols-1 gap-2">
            {expenses.map((e) => (
              <ExpenseRow
                key={e.id}
                expense={e}
                currency={currency}
                finalised={finalised}
                onDelete={() => delExpense.mutate(e.id)}
                onChanged={() => {
                  qc.invalidateQueries({ queryKey: ["expenses", dealId] });
                  qc.invalidateQueries({ queryKey: ["cost-summary", dealId] });
                }}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Add expense dialog */}
      <Dialog open={openExp} onOpenChange={(v) => { setOpenExp(v); if (!v) setPendingReceipt(null); }}>
        <DialogContent className="rounded-sm max-w-lg" data-testid="expense-dialog">
          <DialogHeader><DialogTitle>Log expense</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Description</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={expForm.description} onChange={(e) => setExpForm({ ...expForm, description: e.target.value })} data-testid="expense-desc" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount ({(summary?.currency) || "SGD"})</Label><Input type="number" step="0.01" className="rounded-sm mt-1.5 bg-secondary/40" value={expForm.amount} onChange={(e) => setExpForm({ ...expForm, amount: e.target.value })} data-testid="expense-amount" /></div>
              <div><Label>Date</Label><Input type="date" className="rounded-sm mt-1.5 bg-secondary/40" value={expForm.incurred_at} onChange={(e) => setExpForm({ ...expForm, incurred_at: e.target.value })} /></div>
              <div>
                <Label>Category</Label>
                <Select value={expForm.category} onValueChange={(v) => setExpForm({ ...expForm, category: v })}>
                  <SelectTrigger className="rounded-sm mt-1.5 bg-secondary/40" data-testid="expense-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORY_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Vendor</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={expForm.vendor} onChange={(e) => setExpForm({ ...expForm, vendor: e.target.value })} /></div>
              <div className="col-span-2"><Label>Reference / bill #</Label><Input className="rounded-sm mt-1.5 bg-secondary/40" value={expForm.reference} onChange={(e) => setExpForm({ ...expForm, reference: e.target.value })} /></div>
              <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} className="rounded-sm mt-1.5 bg-secondary/40" value={expForm.notes} onChange={(e) => setExpForm({ ...expForm, notes: e.target.value })} /></div>
              <div className="col-span-2">
                <Label>Receipt <span className="text-[10px] text-muted-foreground uppercase tracking-widest">— image or PDF, max 8&nbsp;MB</span></Label>
                {!pendingReceipt ? (
                  <label className="mt-1.5 flex items-center justify-center gap-2 rounded-sm border border-dashed border-border bg-secondary/30 px-3 py-4 text-sm text-muted-foreground cursor-pointer hover:border-primary hover:text-foreground transition-colors" data-testid="expense-receipt-picker">
                    <Upload className="h-4 w-4" />
                    <span>Attach receipt file</span>
                    <input
                      type="file"
                      accept={RECEIPT_ACCEPT}
                      className="sr-only"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (f.size > 8 * 1024 * 1024) { toast.error("File too large — max 8 MB."); return; }
                        setPendingReceipt(f);
                      }}
                      data-testid="expense-receipt-input"
                    />
                  </label>
                ) : (
                  <div className="mt-1.5 flex items-center justify-between gap-2 rounded-sm border border-border bg-secondary/40 px-3 py-2" data-testid="expense-receipt-picked">
                    <div className="flex items-center gap-2 min-w-0">
                      <Paperclip className="h-4 w-4 text-primary shrink-0" />
                      <div className="truncate text-sm">{pendingReceipt.name}</div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground shrink-0">{(pendingReceipt.size / 1024).toFixed(0)} KB</div>
                    </div>
                    <button onClick={() => setPendingReceipt(null)} className="text-muted-foreground hover:text-red-400" data-testid="expense-receipt-remove"><X className="h-4 w-4" /></button>
                  </div>
                )}
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" checked={expForm.is_unplanned} onChange={(e) => setExpForm({ ...expForm, is_unplanned: e.target.checked })} data-testid="expense-unplanned" />
                <Label className="text-xs">Mark as unplanned / miscellaneous cost</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpenExp(false)}>Cancel</Button>
            <Button className="rounded-sm bg-primary" onClick={() => addExpense.mutate()} disabled={!expForm.description || !expForm.amount || addExpense.isPending} data-testid="expense-save">
              <DollarSign className="h-4 w-4 mr-2" />{addExpense.isPending ? "Saving…" : "Log expense"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ExpenseRow({ expense, currency, finalised, onDelete, onChanged }) {
  const e = expense;
  const hasReceipt = !!e.receipt?.storage_path;
  const fileRef = useRef(null);

  const uploadReceipt = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("File too large — max 8 MB."); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/expenses/${e.id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Receipt attached");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
  };

  const removeReceipt = async () => {
    try {
      await api.delete(`/expenses/${e.id}/receipt`);
      toast.success("Receipt removed");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const viewReceipt = async () => {
    // Fetch as a blob so the auth header is included, then open in a new tab
    try {
      const resp = await api.get(`/expenses/${e.id}/receipt`, { params: { inline: true }, responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      window.open(url, "_blank");
      // Revoke after a minute — long enough for the browser to open + render
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      toast.error("Could not open receipt");
    }
  };

  return (
    <div className="grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-3 rounded-sm border border-border bg-secondary/20 px-3 py-2" data-testid={`expense-row-${e.id}`}>
      <div>
        <div className="text-sm font-medium flex items-center gap-1.5">
          {e.description}
          {hasReceipt && (
            <Badge className="rounded-sm text-[9px] bg-primary/10 text-primary border-primary/40 px-1 py-0" data-testid={`expense-receipt-badge-${e.id}`}>
              <Paperclip className="h-2.5 w-2.5 mr-0.5" /> Receipt
            </Badge>
          )}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
          {e.category}{e.is_unplanned ? " · Unplanned" : ""}{e.vendor ? ` · ${e.vendor}` : ""}{e.reference ? ` · #${e.reference}` : ""}
        </div>
      </div>
      <div className="text-xs text-muted-foreground">{e.incurred_at}</div>
      <div className="text-sm font-bold">{fmt(e.amount, currency)}</div>
      <div className="flex items-center gap-1">
        {hasReceipt ? (
          <>
            <button onClick={viewReceipt} className="text-muted-foreground hover:text-primary p-1" title={`View ${e.receipt.original_filename}`} data-testid={`expense-receipt-view-${e.id}`}>
              <Eye className="h-4 w-4" />
            </button>
            {!finalised && (
              <button onClick={removeReceipt} className="text-muted-foreground hover:text-red-400 p-1" title="Remove receipt" data-testid={`expense-receipt-delete-${e.id}`}>
                <X className="h-4 w-4" />
              </button>
            )}
          </>
        ) : (
          !finalised && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept={RECEIPT_ACCEPT}
                className="sr-only"
                onChange={(ev) => { uploadReceipt(ev.target.files?.[0]); ev.target.value = ""; }}
                data-testid={`expense-receipt-input-${e.id}`}
              />
              <button onClick={() => fileRef.current?.click()} className="text-muted-foreground hover:text-primary p-1" title="Attach receipt" data-testid={`expense-receipt-attach-${e.id}`}>
                <Paperclip className="h-4 w-4" />
              </button>
            </>
          )
        )}
      </div>
      {!finalised ? (
        <button onClick={onDelete} className="text-muted-foreground hover:text-red-400" data-testid={`expense-delete-${e.id}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      ) : <div className="w-4" />}
    </div>
  );
}

function MiniKpi({ label, value, tone = "neutral", sub }) {
  const toneClass = { neutral: "text-foreground", green: "text-green-400", red: "text-red-400", primary: "text-primary" }[tone];
  return (
    <div className="rounded-sm border border-border bg-secondary/20 px-3 py-2">
      <div className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">{label}</div>
      <div className={`font-display text-lg font-black mt-0.5 ${toneClass}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}
